import { IChatMessage } from '@app-types/websocket/chat.types';

// The server returns at most 50 messages per history request.
export const CHAT_HISTORY_PAGE_SIZE = 50;

// The server answers with the numMsgsBack newest messages whose id is <= startId.
export interface IChatHistoryWindow {
  startId: number;
  numMsgsBack: number;
}

function isMessageId(id: unknown): id is number {
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0;
}

function sortedMessageIds(messages: IChatMessage[] | undefined): number[] {
  const ids: number[] = [];
  let sorted = true;
  for (const message of Array.isArray(messages) ? messages : []) {
    if (message && isMessageId(message.id)) {
      if (ids.length > 0 && message.id < ids[ids.length - 1]) {
        sorted = false;
      }
      ids.push(message.id);
    }
  }
  // The reducer keeps messages sorted by id, but state persisted by an older
  // version is restored without passing through it.
  return sorted ? ids : ids.sort((a, b) => a - b);
}

/**
 * Message ids in a chat are numbered 1, 2, 3, ..., so an id missing between
 * the oldest and the newest loaded message is a hole. History older than the
 * oldest loaded message is not a hole; scrolling loads it on demand.
 *
 * Returns the request that fills the newest hole, or undefined when the loaded
 * history is contiguous. Ids the server is known not to have are skipped.
 */
export function findNewestHistoryGap(
  messages: IChatMessage[] | undefined,
  unavailableIds?: Set<number>
): IChatHistoryWindow | undefined {
  const ids = sortedMessageIds(messages);
  for (let index = ids.length - 1; index > 0; index--) {
    const lowerId = ids[index - 1];
    let missingId = ids[index] - 1;
    while (missingId > lowerId && unavailableIds !== undefined && unavailableIds.has(missingId)) {
      missingId--;
    }
    if (missingId > lowerId) {
      return {
        startId: missingId,
        numMsgsBack: Math.min(CHAT_HISTORY_PAGE_SIZE, missingId - lowerId),
      };
    }
  }
  return undefined;
}

/** Ids in a requested window that are not loaded. */
export function missingIdsInWindow(messages: IChatMessage[] | undefined, window: IChatHistoryWindow): number[] {
  const loaded = new Set(sortedMessageIds(messages));
  const missing: number[] = [];
  const oldestId = Math.max(1, window.startId - window.numMsgsBack + 1);
  for (let id = window.startId; id >= oldestId; id--) {
    if (!loaded.has(id)) {
      missing.push(id);
    }
  }
  return missing;
}



// WEBPACK FOOTER //
// ./src/services/chat/chatHistoryGaps.ts